import Dialog from '../../common/component/form/Dialog';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import TextInput from '../../common/component/form/TextInput';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {checkValidation, isValidRunTime} from '../../common/util/ValidationUtil';
import {isPositiveNumeric} from '../util/TrainingUtilities';
import runApi from '../api/runApi';

export default function RunDialog({
                                      open,
                                      onSave,
                                      onCancel,
                                      run: initialRun,
                                      numberOfInterims,
                                      trainingId,
                                      nextIndex
                                  }) {
    const [t] = useTranslation();
    const [run, setRun] = useState();
    const [validation, setValidation] = useState();
    const [isValidated, setIsValidated] = useState(false);

    useEffect(() => {
        setIsValidated(false);
        if (open) {
            setRun(initialRun ? {...initialRun} : {
                run: null,
                athlete: {
                    shortName: null,
                    fullName: null
                },
                team: null,
                notes: null,
                ski: null,
                binding: null,
                boots: null,
                plate: null,
                times: [],
                idx: nextIndex,
                trainingId: trainingId
            });
        }
    }, [open]);

    useEffect(() => {
        if (isValidated) {
            validate();
        }
    }, [run]);

    const handleChange = (field, value) => {
        setRun({
            ...run,
            [field]: value
        });
    };

    const handleAthleteChange = (field, value) => {
        setRun({
            ...run,
            athlete: {
                ...run?.athlete,
                [field]: value
            }
        });
    };

    const handleTimeChange = (index, value) => {
        const newTimes = [...run.times];
        const updatedTime = run.times && run.times.find(time => time.sequenceIndex === index);
        if (updatedTime) {
            updatedTime['ms'] = value;
        } else {
            newTimes.push({
                ms: value,
                sequenceIndex: index,
                endTime: index === 0
            });
        }

        setRun({
            ...run,
            times: newTimes
        });
    };

    const validate = () => {
        const times = {};
        for (let i = 0; i <= numberOfInterims; i++) {
            const runTime = run.times.find(time => time.sequenceIndex === i);

            // If time is filled it must be a valid time
            times[i] = !runTime || !runTime.ms || isValidRunTime(runTime.ms);
        }
        const newValidation = {
            name: !!run.athlete.shortName,
            run: isPositiveNumeric(run.run),
            times: times
        };

        setIsValidated(true);
        setValidation(newValidation);
        return checkValidation(newValidation);
    };

    const handleSave = () => {
        if (validate()) {
            const runToSave = {
                ...run,
                athleteShortName: run.athlete?.shortName,
                athleteFullName: run.athlete?.fullName
            };
            delete runToSave.athlete;
            runApi.createOrUpdateRun(runToSave).then(response => {
                if (response.data.success) {
                    onSave();
                }
            });
        }
    }

    return (
        <Dialog
            open={open}
            title={run && run.id ? t('Run.EDIT') : t('Run.ADD')}
            footer={
                <>
                    <button
                        onClick={onCancel}
                        type="button"
                        className="btn btn-transparent-primary">
                        <FontAwesomeIcon icon="times" className="mr-2"/>
                        {t('Button.CANCEL')}
                    </button>

                    <button
                        disabled={false}
                        onClick={handleSave}
                        type="button"
                        className={'btn btn-primary'}>
                        <FontAwesomeIcon icon="check" className="mr-2"/>
                        {t('Button.SAVE')}
                    </button>
                </>
            }
        >
            <TextInput
                label={t('Training.IDX')}
                value={run && run.idx}
                readonly={true}
            />
            <TextInput
                label={t('Training.TEAM')}
                value={run && run.team}
                onChange={value => handleChange('team', value)}
            />
            <TextInput
                label={`${t('Training.ATHLETE_SHORT_NAME')}*`}
                value={run && run.athlete.shortName}
                onChange={value => handleAthleteChange('shortName', value)}
                required={isValidated && !validation?.name}
            />

            <TextInput
                label={t('Training.ATHLETE_FULL_NAME')}
                value={run && run.athlete.fullName}
                onChange={value => handleAthleteChange('fullName', value)}
            />
            <TextInput
                label={`${t('Training.RUN')}*`}
                value={run && run.run}
                onChange={value => handleChange('run', value)}
                showError={isValidated && !validation.run}
                errorMessage={t('Error.INT_ONLY_REQ')}
            />
            {
                [...Array(numberOfInterims)].map((e, index) =>
                    <TextInput
                        key={index}
                        label={`${t('Training.INTERIM')} ${index + 1}`}
                        value={run && run.times && run.times.find(time => time.sequenceIndex === index + 1)?.ms}
                        onChange={value => handleTimeChange(index + 1, value)}
                        showError={isValidated && !validation.times[index + 1]}
                        errorMessage={t('Error.UNPROCESSABLE_VALUE_TIME')}
                    />)
            }
            <TextInput
                label={`${t('Training.FINAL')}`}
                value={run && run.times.find(time => time.sequenceIndex === 0)?.ms}
                onChange={value => handleTimeChange(0, value)}
                showError={isValidated && !validation.times[0]}
                errorMessage={t('Error.UNPROCESSABLE_VALUE_TIME')}
            />
            <TextInput
                label={t('Training.NOTES')}
                value={run && run.notes}
                onChange={value => handleChange('notes', value)}
            />
            <TextInput
                label={t('Training.SKI')}
                value={run && run.ski}
                onChange={value => handleChange('ski', value)}
            />

            <TextInput
                label={t('Training.BOOTS')}
                value={run && run.boots}
                onChange={value => handleChange('boots', value)}
            />
            <TextInput
                label={t('Training.BINDING')}
                value={run && run.binding}
                onChange={value => handleChange('binding', value)}
            />

            <TextInput
                label={t('Training.PLATE')}
                value={run && run.plate}
                onChange={value => handleChange('plate', value)}
            />

        </Dialog>
    );
}